// @spec AI-STR-032
'use client';

import { useUser } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BookOpen, Database, FileText, ShieldAlert } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CreateKnowledgeBaseDialog,
  KnowledgeBaseDocuments,
  KnowledgeBaseList,
  useCreateKnowledgeBase,
  useDeleteDocument,
  useDeleteKnowledgeBase,
  useKnowledgeBase,
  useKnowledgeBases,
  useKnowledgeBaseStats,
  useReindexKnowledgeBase,
  useUploadDocument,
  UploadDocumentDialog,
} from '@/features/admin';
import type { KnowledgeBase, KnowledgeBaseCategory, KnowledgeDocument } from '@/features/admin';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/admin/knowledge-bases')({
  component: KnowledgeBasesPage,
});

/**
 * Check if user has Super Admin role from Clerk metadata.
 */
function isSuperAdmin(user: ReturnType<typeof useUser>['user']): boolean {
  if (!user) return false;
  const publicMetadata = user.publicMetadata as Record<string, unknown> | undefined;
  return publicMetadata?.role === 'super_admin';
}

function StatsCard({
  title,
  value,
  icon,
  isLoading,
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <p className="text-2xl font-bold">{value}</p>
        )}
      </CardContent>
    </Card>
  );
}

function KnowledgeBasesPage() {
  const { user, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  // State
  const [selectedKbId, setSelectedKbId] = useState<string>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteKbConfirm, setDeleteKbConfirm] = useState<KnowledgeBase | null>(null);
  const [deleteDocConfirm, setDeleteDocConfirm] = useState<KnowledgeDocument | null>(null);

  // Queries
  const { data: kbList, isLoading: isLoadingList } = useKnowledgeBases();
  const { data: selectedKb, isLoading: isLoadingDetails } = useKnowledgeBase(selectedKbId);
  const { data: stats, isLoading: isLoadingStats } = useKnowledgeBaseStats();

  // Mutations
  const { mutateAsync: createKb, isPending: isCreating } = useCreateKnowledgeBase();
  const { mutateAsync: deleteKb, isPending: isDeleting } = useDeleteKnowledgeBase();
  const { mutateAsync: uploadDoc, isPending: isUploading } = useUploadDocument();
  const { mutateAsync: deleteDoc, isPending: isDeletingDoc } = useDeleteDocument();
  const { mutateAsync: reindex, isPending: isReindexing } = useReindexKnowledgeBase();

  // Check Super Admin access
  const hasAccess = isSuperAdmin(user);

  // Handlers
  const handleCreateKb = useCallback(
    async (data: {
      name: string;
      category: KnowledgeBaseCategory;
      description?: string | null;
    }) => {
      try {
        const newKb = await createKb(data);
        setSelectedKbId(newKb.id);
        toast.success(i18n.t('admin:toast.knowledgeBaseCreated'), {
          description: `${data.name} has been created successfully.`,
        });
      } catch (error) {
        toast.error(i18n.t('admin:toast.failedToCreateKnowledgeBase'), {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
        throw error;
      }
    },
    [createKb]
  );

  const handleDeleteKb = useCallback(async () => {
    if (!deleteKbConfirm) return;
    try {
      await deleteKb(deleteKbConfirm.id);
      if (selectedKbId === deleteKbConfirm.id) {
        setSelectedKbId(undefined);
      }
      toast.success(i18n.t('admin:toast.knowledgeBaseDeleted'), {
        description: `${deleteKbConfirm.name} has been deleted.`,
      });
    } catch (error) {
      toast.error(i18n.t('admin:toast.failedToDeleteKnowledgeBase'), {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setDeleteKbConfirm(null);
    }
  }, [deleteKbConfirm, deleteKb, selectedKbId]);

  const handleUploadDoc = useCallback(
    async (
      file: File,
      title: string,
      regulationRef: string | undefined,
      language: 'en-US' | 'pl-PL' | 'cs-CZ'
    ) => {
      if (!selectedKbId) return;
      try {
        await uploadDoc({
          kbId: selectedKbId,
          file,
          title,
          language,
          ...(regulationRef !== undefined && { regulationRef }),
        });
        toast.success(i18n.t('admin:toast.documentUploaded'), {
          description: 'Document is being indexed in the background.',
        });
      } catch (error) {
        toast.error(i18n.t('admin:toast.uploadFailed'), {
          description: error instanceof Error ? error.message : 'Failed to upload document',
        });
        throw error;
      }
    },
    [selectedKbId, uploadDoc]
  );

  const handleDeleteDoc = useCallback(async () => {
    if (!deleteDocConfirm || !selectedKbId) return;
    try {
      await deleteDoc({ kbId: selectedKbId, docId: deleteDocConfirm.id });
      toast.success(i18n.t('admin:toast.documentDeleted'), {
        description: `${deleteDocConfirm.title} has been deleted.`,
      });
    } catch (error) {
      toast.error(i18n.t('admin:toast.failedToDeleteDocument'), {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setDeleteDocConfirm(null);
    }
  }, [deleteDocConfirm, selectedKbId, deleteDoc]);

  const handleReindex = useCallback(
    async (kb: KnowledgeBase) => {
      try {
        const result = await reindex(kb.id);
        toast.success(i18n.t('admin:toast.reindexingStarted'), {
          description: `${String(result.documentCount)} documents are being reindexed.`,
        });
      } catch (error) {
        toast.error(i18n.t('admin:toast.reindexFailed'), {
          description: error instanceof Error ? error.message : 'Failed to start reindexing',
        });
      }
    },
    [reindex]
  );

  // Loading state for user
  if (!userLoaded) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-4 w-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  // 403 if not Super Admin
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-lg text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-4">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">{t('accessDenied.title')}</h1>
          <p className="text-muted-foreground mb-6">
            {t('accessDenied.superAdminRequired')}
          </p>
          <button
            type="button"
            className="text-primary-600 hover:underline"
            onClick={() => {
              void navigate({ to: '/dashboard' });
            }}
          >
            {t('accessDenied.returnToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  const existingCategories = (kbList?.items ?? []).map((kb) => kb.category);

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <BookOpen className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Knowledge Bases</h1>
            <p className="text-sm text-muted-foreground">
              Manage platform-wide regulatory knowledge for AI Advisor
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <StatsCard
            title="Knowledge Bases"
            value={stats?.totalKnowledgeBases ?? 0}
            icon={<Database className="h-4 w-4 text-muted-foreground" />}
            isLoading={isLoadingStats}
          />
          <StatsCard
            title="Total Documents"
            value={stats?.totalDocuments ?? 0}
            icon={<FileText className="h-4 w-4 text-muted-foreground" />}
            isLoading={isLoadingStats}
          />
          <StatsCard
            title="Indexed Chunks"
            value={stats?.totalChunks.toLocaleString() ?? 0}
            icon={<BookOpen className="h-4 w-4 text-muted-foreground" />}
            isLoading={isLoadingStats}
          />
          <StatsCard
            title="Indexing In Progress"
            value={stats?.indexingInProgress ?? 0}
            icon={<Database className="h-4 w-4 text-muted-foreground" />}
            isLoading={isLoadingStats}
          />
        </div>

        {/* Main content */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Knowledge base list */}
          <div className="lg:col-span-1">
            <KnowledgeBaseList
              knowledgeBases={kbList?.items}
              isLoading={isLoadingList}
              selectedId={selectedKbId}
              onSelect={(kb) => {
                setSelectedKbId(kb.id);
              }}
              onCreateNew={() => {
                setCreateDialogOpen(true);
              }}
              onDelete={(kb) => {
                setDeleteKbConfirm(kb);
              }}
              onReindex={(kb) => {
                void handleReindex(kb);
              }}
              isDeleting={isDeleting}
              isReindexing={isReindexing}
            />
          </div>

          {/* Documents panel */}
          <div className="lg:col-span-2">
            <KnowledgeBaseDocuments
              knowledgeBase={selectedKb}
              documents={selectedKb?.documents}
              isLoading={isLoadingDetails && !!selectedKbId}
              onUpload={() => {
                setUploadDialogOpen(true);
              }}
              onDeleteDocument={(doc) => {
                setDeleteDocConfirm(doc);
              }}
              isDeleting={isDeletingDoc}
            />
          </div>
        </div>
      </div>

      {/* Create Knowledge Base Dialog */}
      <CreateKnowledgeBaseDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateKb}
        isSubmitting={isCreating}
        existingCategories={existingCategories}
      />

      {/* Upload Document Dialog */}
      {selectedKb && (
        <UploadDocumentDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          knowledgeBaseName={selectedKb.name}
          onUpload={handleUploadDoc}
          isUploading={isUploading}
        />
      )}

      {/* Delete Knowledge Base Confirmation */}
      <AlertDialog
        open={!!deleteKbConfirm}
        onOpenChange={() => {
          setDeleteKbConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('routeErrors.deleteKnowledgeBase')}</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteKbConfirm?.name} and all its documents. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('routeErrors.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDeleteKb()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Document Confirmation */}
      <AlertDialog
        open={!!deleteDocConfirm}
        onOpenChange={() => {
          setDeleteDocConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('routeErrors.deleteDocument')}</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteDocConfirm?.title}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('routeErrors.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDeleteDoc()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
